import {
  Button,
  createStyles,
  IconButton,
  makeStyles,
  Popover,
  Theme,
  Typography
} from '@material-ui/core';
import { InfoOutlined, OpenInNewRounded } from '@material-ui/icons';
import { ReactNode, useState } from 'react';
import { Link as RRLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end'
    },
    children: {
      maxWidth: 400,
      textAlign: 'left',
      fontSize: theme.typography.pxToRem(12)
    },
    iconButton: {
      marginLeft: theme.spacing(0.25)
    },
    icon: {
      fontSize: 16
    },
    link: {
      marginTop: theme.spacing(1)
    }
  })
);

interface HelpTooltipProps {
  link?: string;
  children?: ReactNode;
}

export default function HelpTooltip({ link, children }: HelpTooltipProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    setAnchorElement(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorElement(null);
  };

  const openHelpTooltip = anchorElement !== null;

  return (
    <span>
      <IconButton
        className={classes.iconButton}
        size="small"
        onClick={handlePopoverOpen}
        onMouseEnter={handlePopoverOpen}
      >
        <InfoOutlined className={classes.icon} color="action" />
      </IconButton>
      <Popover
        open={openHelpTooltip}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        onClose={handlePopoverClose}
      >
        <div className={classes.container}>
          <Typography className={classes.children}>{children}</Typography>
          {link && (
            <Button
              className={classes.link}
              component={RRLink}
              to={link}
              target="_blank"
              color="primary"
              variant="outlined"
              size="small"
              endIcon={<OpenInNewRounded />}
            >
              {t('helpTooltip.learnMore')}
            </Button>
          )}
        </div>
      </Popover>
    </span>
  );
}
